import { Component, OnDestroy, OnInit } from '@angular/core';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AuthenticationService } from './core/services/authentication.service';

import { IdlePopupComponent } from '@shared/components/idle-popup/idle-popup.component';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LocalStorageService } from '@gah-core/services/local-storage.service';
import { UserStoreService } from '@gah-core/services/user-store.service';
import { NGXLogger } from 'ngx-logger';
import { filter, first, tap } from 'rxjs/operators';
import { AppUtilService } from '@gah-core/services/app-util.service';
import { CanDeactivateState } from '@gah-core/guards/pending-changes.guard';
import { GoogleAnalyticsService } from '@shared/services/googleanalytics.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'globalassethub';
  userName: string = '';
  response: any;
  lastPing?: Date;
  arraySubs: Subscription[] = [];
  tokenMonitorInterval: any; // setInterval instance for token monitor
  private readonly TOKEN_MONITOR_INTERVAL: number = 60 * 1000; // Checks token every minute (60s)
  showHeader: boolean = true;
  showFooter: boolean = true;

  constructor(
    public dialog: MatDialog,
    public translate: TranslateService,
    private userStoreService: UserStoreService,
    private idle: Idle,
    private authService: AuthenticationService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private log: NGXLogger,
    private utilService: AppUtilService,
    private googleAnalyticsService: GoogleAnalyticsService
  ) {}

  ngOnInit() {
  // Initialize GTM dynamically based on environment
    this.googleAnalyticsService.initializeGTM();
    this.googleAnalyticsService.addNoScriptIframe();
    
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        if (event.url == '/assets/viewassets' || event.url == '/maintenance') {
          this.showHeader = false;
          this.showFooter = false;
        }
      }
    });
    // if the user clicks the back button, ask the CanDeactivateGuard to defend against this.
    window.onpopstate = () => (CanDeactivateState.defendAgainstBrowserBackButton = true);
    // Upon successful navigation, ensure that the CanDeactivateGuard no longer defends against back button clicks
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        tap(() => (CanDeactivateState.defendAgainstBrowserBackButton = false))
      )
      .subscribe((val) => {
        if (this.userStoreService.userState?.isAuthenticated) {
          this.idle.setIdle(30 * 60); // 10 minutes -- at this point popup will appear.
          this.idle.setTimeout(35 * 60); // 5 minutes -- at this point user is logged out.
          this.idle.watch();
        } else {
          this.idle.stop();
        }
      });
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.arraySubs.push(
      this.idle.onIdleEnd.subscribe(() => {
        this.idle.stop();
      })
    );
    this.arraySubs.push(
      this.idle.onIdleEnd.subscribe(() => {
        this.idle.stop();
      })
    );
    this.arraySubs.push(
      this.idle.onTimeout.subscribe(() => {
        this.authService.logout(true);
        this.dialog.closeAll();
      })
    );
    this.arraySubs.push(
      this.idle.onIdleStart.subscribe(() => {
        this.dialog.closeAll();
        this.openIdleDialog();
      })
    );

    // Sets translations related configs.
    this.translate.setDefaultLang('EN');
    const savedLang = this.localStorageService.get('LANG') ?? 'EN';
    this.translate.use(this.utilService.getRegularExpForLangCodes() ? savedLang : 'EN');
    this.translate.onLangChange.subscribe((language) => {
      this.localStorageService.set('LANG', language.lang);
    });
    this.monitorTokenExpiry();
  }

  /**
   * Triggers idle popup and pushes contextual data to it.
   */
  openIdleDialog() {
    this.dialog.open(IdlePopupComponent, {
      data: {
        timeRemaining: this.lastPing,
      },
      disableClose: true,
      panelClass: 'gah-mat-dialog__panel',
      backdropClass: 'gah-mat-dialog__backdrop',
      hasBackdrop: true,
    });
  }

  monitorTokenExpiry() {
    // This block of code is to check the token expiry on first load of application.
    if (this.userStoreService.userState?.isAuthenticated) {
      if (this.authService.isTokenExpired()) {
        // Deleting all local storage items
        this.localStorageService.removeAll();
        this.authService.logout(true);
      }
    }
    // Setting interval to check token expiry every 60 seconds
    this.tokenMonitorInterval = setInterval(() => {
      let expiryAt: number = this.localStorageService.get('T_EA');
      if (expiryAt) {
        let currentTime = Math.floor(Date.now() / 1000);
        if (currentTime > Math.floor(expiryAt / 1000)) {
          this.authService.logout(true);
        } else if (currentTime > Math.floor(expiryAt / 1000) - 300) {
          // 300 ~ 5 minutes
          this.authService
            .refreshToken()
            .pipe(first())
            .subscribe({
              error: (err) => {
                this.log.error('Error refreshing token');
              },
            });
        }
      }
    }, this.TOKEN_MONITOR_INTERVAL);
  }

  ngOnDestroy() {
    this.arraySubs.forEach((sub) => sub.unsubscribe());
  }
}